/**
 * Semantic Color Token System
 * 
 * This file defines semantic color tokens that automatically adapt to light/dark mode.
 * These tokens provide a consistent, scalable color system across the entire application.
 */

import { PaletteMode } from '@mui/material';

// ============================================================================
// COLOR DEFINITIONS
// ============================================================================

/**
 * Base color palette - raw color values
 * These are the foundation colors that semantic tokens will reference
 */
const baseColors = {
  // Primary brand colors
  blue: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2E86AB', // Main brand blue
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
  },
  purple: {
    50: '#f3e5f5',
    100: '#e1bee7',
    200: '#ce93d8',
    300: '#ba68c8',
    400: '#ab47bc',
    500: '#A23B72', // Main brand purple
    600: '#8e24aa',
    700: '#7b1fa2',
    800: '#6a1b9a',
    900: '#4a148c',
  },
  
  // Semantic colors
  green: {
    50: '#e8f5e9',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50',
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },
  yellow: {
    50: '#fffde7',
    100: '#fff9c4',
    200: '#fff59d',
    300: '#fff176',
    400: '#ffee58',
    500: '#ffeb3b',
    600: '#fdd835',
    700: '#fbc02d',
    800: '#f9a825',
    900: '#f57f17',
  },
  orange: {
    50: '#fff3e0',
    100: '#ffe0b2',
    200: '#ffcc80',
    300: '#ffb74d',
    400: '#ffa726',
    500: '#ff9800',
    600: '#fb8c00',
    700: '#f57c00',
    800: '#ef6c00',
    900: '#e65100',
  },
  red: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336',
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
  },
  
  // Neutral grays
  gray: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  
  // Chart-specific colors
  chart: {
    blue: '#2196f3',
    cyan: '#00bcd4',
    teal: '#009688',
    green: '#4caf50',
    lime: '#cddc39',
    yellow: '#ffeb3b',
    amber: '#ffc107',
    orange: '#ff9800',
    deepOrange: '#ff5722',
    red: '#f44336',
    pink: '#e91e63',
    purple: '#9c27b0',
    deepPurple: '#673ab7',
    indigo: '#3f51b5',
  },
};

// ============================================================================
// SEMANTIC TOKEN DEFINITIONS
// ============================================================================

/**
 * Creates semantic color tokens based on the current theme mode
 */
export const createColorTokens = (mode: PaletteMode) => {
  const isLight = mode === 'light';
  
  return {
    // ========================================================================
    // COMPONENT TOKENS
    // ========================================================================
    component: {
      card: {
        background: isLight ? '#ffffff' : '#1e1e1e',
        border: isLight ? baseColors.gray[300] : baseColors.gray[700],
        hover: isLight ? baseColors.gray[50] : baseColors.gray[800],
        shadow: isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.4)',
      },
      modal: {
        background: isLight ? '#ffffff' : '#1e1e1e',
        overlay: isLight ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)',
        border: isLight ? baseColors.gray[300] : baseColors.gray[700],
        shadow: isLight ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.5)',
      },
      panel: {
        background: isLight ? '#ffffff' : '#1e1e1e',
        backgroundAlt: isLight ? baseColors.gray[50] : '#252525',
        border: isLight ? baseColors.gray[300] : baseColors.gray[700],
        header: isLight ? baseColors.gray[100] : '#2a2a2a',
      },
      sidebar: {
        background: isLight ? '#ffffff' : '#1e1e1e',
        backgroundAlt: isLight ? baseColors.gray[50] : '#252525',
        border: isLight ? baseColors.gray[300] : baseColors.gray[700],
        hover: isLight ? baseColors.gray[100] : baseColors.gray[800],
        active: isLight ? baseColors.blue[50] : baseColors.blue[900],
      },
      dropdown: {
        background: isLight ? '#ffffff' : '#2a2a2a',
        hover: isLight ? baseColors.gray[100] : baseColors.gray[700],
        selected: isLight ? baseColors.blue[50] : baseColors.blue[900],
        border: isLight ? baseColors.gray[300] : baseColors.gray[600],
      },
      input: {
        background: isLight ? '#ffffff' : '#2a2a2a',
        border: isLight ? baseColors.gray[400] : baseColors.gray[600],
        borderFocus: isLight ? baseColors.blue[500] : baseColors.blue[400],
        placeholder: isLight ? baseColors.gray[500] : baseColors.gray[500],
        disabled: isLight ? baseColors.gray[200] : baseColors.gray[800],
      },
      table: {
        headerBackground: isLight ? baseColors.gray[100] : '#2a2a2a',
        rowBackground: isLight ? '#ffffff' : '#1e1e1e',
        rowBackgroundAlt: isLight ? baseColors.gray[50] : '#252525',
        rowHover: isLight ? baseColors.blue[50] : baseColors.gray[800],
        border: isLight ? baseColors.gray[300] : baseColors.gray[700],
      },
      tooltip: {
        background: isLight ? baseColors.gray[800] : baseColors.gray[200],
        text: isLight ? '#ffffff' : baseColors.gray[900],
      },
    },

    // ========================================================================
    // BUTTON TOKENS
    // ========================================================================
    button: {
      primary: {
        background: baseColors.blue[500],
        backgroundHover: baseColors.blue[600],
        backgroundActive: baseColors.blue[700],
        backgroundDisabled: isLight ? baseColors.gray[300] : baseColors.gray[700],
        text: '#ffffff',
        textDisabled: isLight ? baseColors.gray[500] : baseColors.gray[600],
      },
      secondary: {
        background: baseColors.purple[500],
        backgroundHover: baseColors.purple[600],
        backgroundActive: baseColors.purple[700],
        backgroundDisabled: isLight ? baseColors.gray[300] : baseColors.gray[700],
        text: '#ffffff',
        textDisabled: isLight ? baseColors.gray[500] : baseColors.gray[600],
      },
      tertiary: {
        background: 'transparent',
        backgroundHover: isLight ? baseColors.gray[100] : baseColors.gray[800],
        backgroundActive: isLight ? baseColors.gray[200] : baseColors.gray[700],
        border: isLight ? baseColors.gray[400] : baseColors.gray[600],
        text: isLight ? baseColors.gray[900] : baseColors.gray[100],
        textDisabled: isLight ? baseColors.gray[400] : baseColors.gray[600],
      },
      success: {
        background: baseColors.green[600],
        backgroundHover: baseColors.green[700],
        backgroundActive: baseColors.green[800],
        text: '#ffffff',
      },
      warning: {
        background: baseColors.orange[600],
        backgroundHover: baseColors.orange[700],
        backgroundActive: baseColors.orange[800],
        text: '#ffffff',
      },
      error: {
        background: baseColors.red[600],
        backgroundHover: baseColors.red[700],
        backgroundActive: baseColors.red[800],
        text: '#ffffff',
      },
    },

    // ========================================================================
    // TEXT TOKENS
    // ========================================================================
    text: {
      primary: isLight ? baseColors.gray[900] : baseColors.gray[100],
      secondary: isLight ? baseColors.gray[700] : baseColors.gray[400],
      tertiary: isLight ? baseColors.gray[600] : baseColors.gray[500],
      muted: isLight ? baseColors.gray[500] : baseColors.gray[600],
      disabled: isLight ? baseColors.gray[400] : baseColors.gray[700],
      inverse: isLight ? '#ffffff' : baseColors.gray[900],
      link: baseColors.blue[isLight ? 600 : 400],
      linkHover: baseColors.blue[isLight ? 700 : 300],
      success: baseColors.green[isLight ? 700 : 400],
      warning: baseColors.orange[isLight ? 700 : 400],
      error: baseColors.red[isLight ? 700 : 400],
      info: baseColors.blue[isLight ? 600 : 400],
    },

    // ========================================================================
    // SEMANTIC STATE TOKENS
    // ========================================================================
    semantic: {
      success: {
        main: baseColors.green[isLight ? 600 : 500],
        background: isLight ? baseColors.green[50] : baseColors.green[900],
        border: isLight ? baseColors.green[300] : baseColors.green[700],
        text: isLight ? baseColors.green[800] : baseColors.green[200],
      },
      warning: {
        main: baseColors.orange[isLight ? 600 : 500],
        background: isLight ? baseColors.orange[50] : baseColors.orange[900],
        border: isLight ? baseColors.orange[300] : baseColors.orange[700],
        text: isLight ? baseColors.orange[800] : baseColors.orange[200],
      },
      error: {
        main: baseColors.red[isLight ? 600 : 500],
        background: isLight ? baseColors.red[50] : baseColors.red[900],
        border: isLight ? baseColors.red[300] : baseColors.red[700],
        text: isLight ? baseColors.red[800] : baseColors.red[200],
      },
      info: {
        main: baseColors.blue[isLight ? 600 : 500],
        background: isLight ? baseColors.blue[50] : baseColors.blue[900],
        border: isLight ? baseColors.blue[300] : baseColors.blue[700],
        text: isLight ? baseColors.blue[800] : baseColors.blue[200],
      },
    },

    // ========================================================================
    // CHART & DATA VISUALIZATION TOKENS
    // ========================================================================
    chart: {
      // Categorical colors for different data series
      categorical: [
        baseColors.chart.blue,
        baseColors.chart.orange,
        baseColors.chart.green,
        baseColors.chart.red,
        baseColors.chart.purple,
        baseColors.chart.cyan,
        baseColors.chart.pink,
        baseColors.chart.teal,
        baseColors.chart.amber,
        baseColors.chart.indigo,
        baseColors.chart.deepOrange,
        baseColors.chart.lime,
      ],
      
      // Sequential colors for gradients (light to dark)
      sequential: {
        blue: [
          baseColors.blue[100],
          baseColors.blue[300],
          baseColors.blue[500],
          baseColors.blue[700],
          baseColors.blue[900],
        ],
        green: [
          baseColors.green[100],
          baseColors.green[300],
          baseColors.green[500],
          baseColors.green[700],
          baseColors.green[900],
        ],
        red: [
          baseColors.red[100],
          baseColors.red[300],
          baseColors.red[500],
          baseColors.red[700],
          baseColors.red[900],
        ],
        purple: [
          baseColors.purple[100],
          baseColors.purple[300],
          baseColors.purple[500],
          baseColors.purple[700],
          baseColors.purple[900],
        ],
      },
      
      // Diverging colors for showing variance from a midpoint
      diverging: {
        redBlue: [
          baseColors.red[700],
          baseColors.red[400],
          baseColors.gray[300],
          baseColors.blue[400],
          baseColors.blue[700],
        ],
        orangePurple: [
          baseColors.orange[700],
          baseColors.orange[400],
          baseColors.gray[300],
          baseColors.purple[400],
          baseColors.purple[700],
        ],
      },
      
      // Chart-specific elements
      grid: isLight ? baseColors.gray[300] : baseColors.gray[700],
      axis: isLight ? baseColors.gray[700] : baseColors.gray[400],
      label: isLight ? baseColors.gray[800] : baseColors.gray[300],
      tooltip: {
        background: isLight ? baseColors.gray[900] : baseColors.gray[100],
        text: isLight ? '#ffffff' : baseColors.gray[900],
        border: isLight ? baseColors.gray[700] : baseColors.gray[300],
      },
    },

    // ========================================================================
    // BACKGROUND TOKENS
    // ========================================================================
    background: {
      default: isLight ? baseColors.gray[100] : '#0A0A0F',
      paper: isLight ? '#ffffff' : '#0A0A0F',
      elevated: isLight ? '#ffffff' : '#2a2a2a',
      overlay: isLight ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)',
      backdrop: isLight ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 30, 30, 0.9)',
    },

    // ========================================================================
    // BORDER & DIVIDER TOKENS
    // ========================================================================
    border: {
      default: isLight ? baseColors.gray[300] : baseColors.gray[700],
      strong: isLight ? baseColors.gray[400] : baseColors.gray[600],
      subtle: isLight ? baseColors.gray[200] : baseColors.gray[800],
      focus: baseColors.blue[isLight ? 500 : 400],
    },

    // ========================================================================
    // ELEVATION & SHADOW TOKENS
    // ========================================================================
    shadow: {
      sm: isLight ? '0 1px 2px rgba(0, 0, 0, 0.05)' : '0 1px 2px rgba(0, 0, 0, 0.3)',
      md: isLight ? '0 4px 6px rgba(0, 0, 0, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.4)',
      lg: isLight ? '0 10px 15px rgba(0, 0, 0, 0.1)' : '0 10px 15px rgba(0, 0, 0, 0.5)',
      xl: isLight ? '0 20px 25px rgba(0, 0, 0, 0.15)' : '0 20px 25px rgba(0, 0, 0, 0.6)',
    },
  };
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ColorTokens = ReturnType<typeof createColorTokens>;

// Augment the MUI theme to include our custom tokens
declare module '@mui/material/styles' {
  interface Theme {
    tokens: ColorTokens;
  }
  interface ThemeOptions {
    tokens?: ColorTokens;
  }
}

